import { ReactNode, Suspense, lazy, useEffect, useState } from "react";

import { EventObject } from "react-alice-carousel";

import {
  renderNextButton,
  renderPrevButton,
} from "pages/[tenant]/browse/components/NavButtons";
import { VignetteLoadingSkeleton } from "pages/[tenant]/browse/components/Vignette/components/LoadingSkeleton";
import useBackgroudPlayerContent from "pages/[tenant]/browse/hooks/useBackgroudPlayerContent";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import componentLoader from "shared/utils/componentLoader";
import useWindowSize from "shared/utils/hooks/useWindowSize";
import { shouldShowStopReloadingAlert } from "shared/utils/shouldShowStopReloadingAlert";

import { captureException, captureMessage } from "@sentry/nextjs";

import useBillboardPageContext from "../../hooks/useBillboardPageContext";
import BackgroudPlayerContent from "./components/BackgroudPlayerContent";
import { VignetteContainer } from "./styles";

const AliceCarouselImport = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkName: 'AliceCarouselImport', filename: 'AliceCarouselImport' */
        "shared/components/organisms/AliceCarouselImport"
      ),
  ),
);

export interface IVignetteProps {
  aditionalContent?: ReactNode;
  renderBackgroundGradientOutsideContent?: boolean;
}

const Vignette = (props?: IVignetteProps) => {
  const {
    highlights,
    setGroup: setActiveGroup,
    group: activeGroup,
    videoTimeDuration,
    setVideoTimeDuration,
    setBackgroundContent,
  } = useBillboardPageContext();

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const groups = highlights?.map((group) => group?.content);

  const handleSetActiveGroupMedia = ({ item }: EventObject) => {
    try {
      if (typeof item !== "number") throw new Error("Item must be a number");

      const itemIndex = item + 1;

      setActiveGroup(groups?.[itemIndex] ?? groups?.[0]);
      setActiveSlideIndex(!!groups?.[itemIndex] ? itemIndex : 0);
    } catch (error) {
      console.log(error);
      captureException(error);
      captureMessage("Error ~ Vignette::handleSetActiveGroupMedia");
    } finally {
      setVideoTimeDuration(10000);
    }
  };

  useEffect(() => {
    if (!activeGroup && Array?.isArray(groups) && groups?.length > 0)
      setActiveGroup(groups?.[0]);
  }, [groups, activeGroup]);

  useEffect(() => {
    const isVideoAvailable =
      activeGroup?.cover_video?.player ??
      activeGroup?.banner_video?.player ??
      activeGroup?.billboard_video_link;

    setBackgroundContent(!!isVideoAvailable ? "video" : "image");
  }, [activeGroup]);

  useBackgroudPlayerContent();

  const handleMount = async () => {
    if (!shouldShowStopReloadingAlert()) return;

    if (sessionStorage.getItem("should-show-alert") !== "false")
      alert(
        "Identificamos que você está passando por recarregamentos consecutivos. Já estamos trabalhando para a solução desse problema, que afeta uma parcela muito pequena de usuários. Até lá, recomendamos que você tente acessar por outro navegador. Agradecemos sua compreensão :)",
      );

    window.location.reload();
    for (let index = 0; index < 10; index++) {
      await new Promise((resolve) => setTimeout(() => resolve(null), 100)); // 100 ms delay
      window.stop();
    }

    sessionStorage.setItem("should-show-alert", "false");
  };

  const hasMoreThanOneHightlight = groups?.length > 1;

  const [activeSlideIndex, setActiveSlideIndex] = useState(() => {
    const currentIndex = groups?.findIndex(
      (group) => group?.id === activeGroup?.id,
    );

    return currentIndex === -1 ? 0 : currentIndex;
  });

  const handlePrevButtonClick = () => {
    setActiveSlideIndex((prevIndex) =>
      prevIndex - 1 < 0 ? groups?.length - 1 : prevIndex - 1,
    );
    setVideoTimeDuration(10000);
  };

  const handleNextButtonClick = () => {
    setActiveSlideIndex((prevIndex) =>
      prevIndex + 1 >= groups?.length ? 0 : prevIndex + 1,
    );
    setVideoTimeDuration(10000);
  };

  return (
    <VignetteContainer>
      <Suspense fallback={<VignetteLoadingSkeleton />}>
        <AliceCarouselImport
          handleMount={handleMount}
          animationType="fadeout"
          renderPrevButton={
            hasMoreThanOneHightlight
              ? () =>
                  renderPrevButton({
                    isDisabled: true,
                    handleClick: handlePrevButtonClick,
                  })
              : () => null
          }
          renderNextButton={
            hasMoreThanOneHightlight
              ? () => renderNextButton({ handleClick: handleNextButtonClick })
              : () => null
          }
          disableSlideInfo
          disableDotsControls={!hasMoreThanOneHightlight}
          disableButtonsControls={!hasMoreThanOneHightlight}
          mouseTracking={false}
          touchTracking={false}
          autoPlay={hasMoreThanOneHightlight}
          infinite
          animationDuration={1000}
          autoPlayInterval={isMobile ? 3000 : videoTimeDuration}
          onSlideChange={handleSetActiveGroupMedia}
          activeIndex={activeSlideIndex}
          items={groups?.map((group) => (
            <ComponentErrorBoundary
              key={`${group?.slug}-${group?.id}-${group?.created_at}`}
            >
              <BackgroudPlayerContent
                aditionalContent={props?.aditionalContent}
                renderBackgroundGradientOutsideContent={
                  props?.renderBackgroundGradientOutsideContent
                }
              />
            </ComponentErrorBoundary>
          ))}
        />
      </Suspense>
    </VignetteContainer>
  );
};

export default Vignette;
